import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Drawer, Loading, cx } from "./StudioShared";
import ActivityTimeline from "@/components/ActivityTimeline";
import { Plus, Download, Upload, Archive, ArchiveRestore, Trash2, Brain, ChevronDown, History } from "lucide-react";

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const PRIORITY_TONE = { critical: "red", high: "gold", normal: "slate", low: "slate" };
const ASSIGN_LABEL = { strategists: "Strategists", departments: "Departments", missions: "Missions", workflows: "Workflows", clients: "Clients", projects: "Projects" };

export default function MemoryCenter() {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [type, setType] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [opts, setOpts] = useState({ departments: [], strategists: [], missions: [], workflows: [] });

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (type) p.set("type", type);
    if (priority) p.set("priority", priority);
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    const [list, m] = await Promise.all([api.get(`/admin/memories?${p}`), api.get("/admin/memories/meta")]);
    setItems(list.data); setMeta(m.data);
  }, [type, priority, status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/admin/departments"), api.get("/admin/strategists"), api.get("/admin/missions"), api.get("/admin/workflows")])
      .then(([d, s, mi, w]) => setOpts({
        departments: d.data.map((x) => ({ id: x.name, label: x.name })),
        strategists: s.data.map((x) => ({ id: x.id, label: x.name })),
        missions: mi.data.map((x) => ({ id: x.id, label: x.name })),
        workflows: w.data.map((x) => ({ id: x.id, label: x.name })),
      })).catch(() => {});
  }, []);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const archive = (r) => act(() => api.post(`/admin/memories/${r.id}/archive`), "Status updated.");
  const del = (r) => { if (window.confirm(`Delete "${r.title}"?`)) act(() => api.delete(`/admin/memories/${r.id}`), "Memory deleted."); };
  const save = async (payload, id) => {
    setBusy(true);
    try {
      if (id) await api.patch(`/admin/memories/${id}`, payload); else await api.post("/admin/memories", payload);
      toast.success(id ? "Memory updated." : "Memory created."); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const exportAll = async () => {
    const { data } = await api.get("/admin/memories/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "aipos-memories.json"; a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${data.count} memories.`);
  };
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const parsed = JSON.parse(await file.text()); const list = Array.isArray(parsed) ? parsed : (parsed.memories || []);
      const r = await api.post("/admin/memories/import", { memories: list }); toast.success(`Imported ${r.data.imported}.`); load();
    } catch { toast.error("Import failed — expected JSON array or {memories:[...]}."); } finally { e.target.value = ""; }
  };

  if (!items || !meta) return <Loading label="Loading memory…" />;
  const c = meta.counts;
  const columns = [
    { key: "title", label: "Memory", render: (r) => (<div className="min-w-0"><div className="text-white font-medium truncate">{r.title}</div><div className="text-slate-500 text-xs truncate">{r.content?.slice(0, 70)}</div></div>) },
    { key: "type", label: "Type", render: (r) => <Badge tone="slate">{r.type}</Badge> },
    { key: "priority", label: "Priority", render: (r) => <Badge tone={PRIORITY_TONE[r.priority]}>{r.priority}</Badge> },
    { key: "visibility", label: "Visibility", render: (r) => r.visibility },
    { key: "tags", label: "Tags", render: (r) => <div className="flex flex-wrap gap-1">{(r.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] text-slate-400 border border-white/10 rounded-sm px-1.5 py-0.5">{t}</span>)}</div> },
    { key: "status", label: "Status", render: (r) => <Badge tone={r.expired ? "red" : r.status === "archived" ? "amber" : "green"}>{r.expired ? "expired" : r.status}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <IconBtn icon={r.status === "archived" ? ArchiveRestore : Archive} title="Archive" testid={`mem-archive-${r.id}`} onClick={() => archive(r)} disabled={busy} />
        <IconBtn icon={Trash2} title="Delete" danger testid={`mem-del-${r.id}`} onClick={() => del(r)} disabled={busy} />
      </div>) },
  ];

  return (
    <StudioPage title="Memory Center" testid="memory-center-page"
      question="Persistent organizational intelligence — typed, prioritized, assignable memory that AIPOS retrieves at execution time."
      actions={
        <div className="flex flex-wrap gap-2">
          <label data-testid="mem-import-btn" className="cursor-pointer flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white rounded-sm"><Upload size={15} /> Import<input type="file" accept="application/json" className="hidden" onChange={onImport} /></label>
          <button onClick={exportAll} data-testid="mem-export-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white rounded-sm"><Download size={15} /> Export</button>
          <button onClick={() => setEditing({})} data-testid="mem-new-btn" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light rounded-sm"><Plus size={15} /> New Memory</button>
        </div>}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Metric label="Total" value={c.total} icon={Brain} testid="mem-count-total" />
        <Metric label="Active" value={c.active} testid="mem-count-active" />
        <Metric label="Critical" value={c.critical} testid="mem-count-critical" />
        <Metric label="Archived" value={c.archived} testid="mem-count-archived" />
        <Metric label="Expired" value={c.expired} testid="mem-count-expired" />
      </div>
      <Panel className="p-3 mb-5 flex flex-wrap items-center gap-2" data-testid="mem-filters">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="mem-type-filter" className={cx(inp, "w-auto")}><option value="">All types</option>{meta.types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="mem-priority-filter" className={cx(inp, "w-auto")}><option value="">Any priority</option>{meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mem-status-filter" className={cx(inp, "w-auto")}><option value="">Any status</option><option value="active">active</option><option value="archived">archived</option></select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search memories…" data-testid="mem-search" className={cx(inp, "flex-1 min-w-[160px]")} />
      </Panel>
      <DataTable testid="mem-table" columns={columns} rows={items} onRowClick={(r) => setEditing(r)} rowTestid={(r) => `mem-row-${r.id}`} empty="No memories yet. Capture your first organizational memory." />
      {editing !== null && <MemoryDrawer memory={editing} opts={opts} allMemories={items} meta={meta} onClose={() => setEditing(null)} onSave={save} busy={busy} />}
    </StudioPage>
  );
}

const IconBtn = ({ icon: Icon, title, onClick, testid, danger, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
    className={cx("p-1.5 border rounded-sm transition-colors disabled:opacity-40", danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/10 text-slate-300 hover:border-gold/50 hover:text-white")}>
    <Icon size={13} />
  </button>
);

function MultiPick({ label, options, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const sel = new Set(value || []);
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); onChange([...n]); };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-btn`} className={cx(inp, "flex items-center justify-between")}>
        <span className="text-slate-300">{sel.size ? `${sel.size} selected` : `Assign ${label.toLowerCase()}`}</span><ChevronDown size={14} className="text-slate-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-[#0A1128] border border-white/10 rounded-sm shadow-xl p-1" data-testid={`${testid}-menu`}>
            {options.length === 0 ? <div className="text-slate-500 text-xs p-2">None available</div> : options.map((o) => (
              <label key={o.id} className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-300 hover:bg-white/5 rounded-sm cursor-pointer">
                <input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} className="accent-gold" /> <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MemoryDrawer({ memory, opts, allMemories, meta, onClose, onSave, busy }) {
  const isEdit = !!memory.id;
  const [f, setF] = useState(() => ({
    title: memory.title || "", content: memory.content || "", type: memory.type || "organization",
    priority: memory.priority || "normal", visibility: memory.visibility || "organization",
    status: memory.status || "active", expires_at: memory.expires_at ? memory.expires_at.slice(0, 10) : "",
    tags: (memory.tags || []).join(", "),
    assignments: { strategists: [], departments: [], missions: [], workflows: [], clients: [], projects: [], ...(memory.assignments || {}) },
    clients_text: (memory.assignments?.clients || []).join(", "),
    projects_text: (memory.assignments?.projects || []).join(", "),
    relationships: memory.relationships || [],
  }));
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState(null);
  const setA = (kind, ids) => setF({ ...f, assignments: { ...f.assignments, [kind]: ids } });

  const submit = () => {
    const payload = {
      title: f.title, content: f.content, type: f.type, priority: f.priority, visibility: f.visibility,
      status: f.status, expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      assignments: { ...f.assignments,
        clients: f.clients_text.split(",").map((x) => x.trim()).filter(Boolean),
        projects: f.projects_text.split(",").map((x) => x.trim()).filter(Boolean) },
      relationships: f.relationships,
    };
    onSave(payload, memory.id);
  };
  const loadVersions = async () => { setShowVersions(true); const r = await api.get(`/admin/memories/${memory.id}/versions`); setVersions(r.data); };

  return (
    <Drawer open onClose={onClose} testid="mem-drawer" title={isEdit ? "Edit Memory" : "New Memory"} subtitle={isEdit ? `v${memory.version} · ${memory.type}` : "Capture organizational intelligence"}>
      <div><SectionLabel className="mb-1">Title</SectionLabel><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="mem-title" className={inp} /></div>
      <div><SectionLabel className="mb-1">Content</SectionLabel><textarea rows={5} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="mem-content" className={inp} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><SectionLabel className="mb-1">Type</SectionLabel><select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="mem-type" className={inp}>{meta.types.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><SectionLabel className="mb-1">Priority</SectionLabel><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="mem-priority" className={inp}>{meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
        <div><SectionLabel className="mb-1">Visibility</SectionLabel><select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })} data-testid="mem-visibility" className={inp}>{meta.visibilities.map((v) => <option key={v} value={v}>{v}</option>)}</select></div>
        <div><SectionLabel className="mb-1">Expires</SectionLabel><input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} data-testid="mem-expires" className={inp} /></div>
      </div>
      <div><SectionLabel className="mb-1">Tags (comma-separated)</SectionLabel><input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="mem-tags" className={inp} /></div>
      <div>
        <SectionLabel className="mb-2">Assignments</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {["strategists", "departments", "missions", "workflows"].map((k) => (
            <MultiPick key={k} label={ASSIGN_LABEL[k]} options={opts[k]} value={f.assignments[k]} onChange={(ids) => setA(k, ids)} testid={`mem-assign-${k}`} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input value={f.clients_text} onChange={(e) => setF({ ...f, clients_text: e.target.value })} placeholder="Client IDs (comma)" data-testid="mem-assign-clients" className={inp} />
          <input value={f.projects_text} onChange={(e) => setF({ ...f, projects_text: e.target.value })} placeholder="Project IDs (comma)" data-testid="mem-assign-projects" className={inp} />
        </div>
      </div>
      <div>
        <SectionLabel className="mb-2">Related memories</SectionLabel>
        <MultiPick label="Memories" options={allMemories.filter((m) => m.id !== memory.id).map((m) => ({ id: m.id, label: m.title }))} value={f.relationships} onChange={(ids) => setF({ ...f, relationships: ids })} testid="mem-relationships" />
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={submit} disabled={busy || !f.title || !f.content} data-testid="mem-save" className="flex-1 bg-gold text-navy px-4 py-2.5 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-50">{isEdit ? "Save changes" : "Create memory"}</button>
        {isEdit && <button onClick={loadVersions} data-testid="mem-versions-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2.5 text-sm rounded-sm hover:border-gold/50"><History size={14} /> History</button>}
      </div>
      {showVersions && (
        <div className="border-t border-white/10 pt-3" data-testid="mem-versions">
          {versions === null ? <Loading /> : versions.length === 0 ? <p className="text-slate-500 text-sm">No prior versions.</p> :
            versions.map((v) => <div key={v.version} className="text-xs text-slate-400 flex justify-between py-1"><span>v{v.version} — {v.snapshot?.title}</span><span className="text-slate-600">{(v.snapshot_at || "").slice(0, 16).replace("T", " ")}</span></div>)}
        </div>
      )}
      {isEdit && (
        <div className="border-t border-white/10 pt-3">
          <ActivityTimeline title="Knowledge Activity" mode="entity" params={{ entity_id: memory.id }} limit={20} testid="mem-activity-timeline" emptyText="No activity recorded yet." />
        </div>
      )}
    </Drawer>
  );
}
